import React, { useCallback, useMemo, useState } from 'react'
import PropTypes, { object } from 'prop-types'
import { useTranslation } from 'react-i18next'

import PortalModal from 'components/Shared/PortalModal'
import ErrorMessage from 'components/Shared/ErrorMessage'

import { actions } from './media'
import { postGeneratedImages } from 'api/generate-images'

import { applyGeneratedVariantsToMedia } from './applyGeneratedVariantsToMedia'
import {
  buildPostGeneratedImagesBody,
  collectSelectedImageIdsFromResult,
  mapGeneratedImagesResponse,
  normalizePostGeneratedImagesResult,
} from './elements/CoCreatePanel/generateImagesTransforms'

import { MediaExpandedModal } from './elements/MediaExpandedModal'
import GeneratedImagesModal from './elements/CoCreatePanel/GeneratedModal'
import { useMediaUploadService } from './MediaUpload.service'
import CoCreateImagePanel from './elements/CoCreatePanel/CoCreateImagePanel'
import useCoCreateImagePanel from './elements/CoCreatePanel/useCoCreateImagePanel'
import MediaListV2 from './elements/MediaListV2/MediaList'
import { UploadButtons } from './elements/UploadButtons'
import { LibraryPickerModal } from 'components/Library/element/LibraryPickerModal'

const MediaUpload = ({
  initialMedia,
  onChange,
  readonly,
  maxItems = Number.MAX_VALUE,
  generatedMediaCount = 0,
  isCampaignRunning,
  eventId,
  tc,
  tcRunId,
  headlines = [],
  onRequestHeadlines,
}) => {
  const { t } = useTranslation()

  const {
    media,
    generatedMediaCount: liveGeneratedMediaCount,
    reachedMaxLimit,
    expandedImage,
    isFirstItem,
    isLastItem,
    state,
    dispatch,
    onNext,
    onBack,
    removeFile,
    removeMediaGroup,
    handleAddFileToCoCreateGroup,
    addLibraryItemToCoCreateGroup,
    replaceLibraryItemInCoCreateGroup,
    handleReplaceFileInCoCreateGroup,
    handleRemoveFileFromCoCreateGroup,
    getInputProps,
    open,
    setExpandedImage,
    addLibraryItems,
  } = useMediaUploadService({
    initialMedia,
    onChange,
    maxItems,
    generatedMediaCount,
    isCampaignRunning,
    eventId,
    tc,
    tcRunId,
  })

  const [selectedSubGroupId, setSelectedSubGroupId] = useState(null)
  const [isLibraryPickerOpen, setIsLibraryPickerOpen] = useState(false)
  const [generatedImagesModalOpen, setGeneratedImagesModalOpen] = useState(false)
  const [isSubmittingSelections, setIsSubmittingSelections] = useState(false)

  const clearGenerationReserve = useCallback(() => {
    dispatch({ type: actions.clearMediaGenerating })
  }, [dispatch])

  const {
    variant: coCreateVariant,
    setVariant: setCoCreateVariant,
    generateImages,
    generatedResult,
    generationFailed,
    fetchStatusFailed,
    checkStatus,
  } = useCoCreateImagePanel({
    eventId,
    tc,
    tcRunId,
    onClearGenerating: clearGenerationReserve,
  })

  const generatedVariants = useMemo(() => {
    const mapped = mapGeneratedImagesResponse(generatedResult ?? [])
    return mapped.length > 0 ? mapped : []
  }, [generatedResult])

  const handleSubmitGeneratedSelections = async (selectedIds) => {
    if (!generatedResult) {
      return { success: false, error: 'No generated images to submit.' }
    }

    const body = buildPostGeneratedImagesBody({
      eventId,
      tc,
      tcRunId,
      generatedResult,
      selectedIds,
    })

    const response = await postGeneratedImages(body)

    if (!response.success) {
      return response
    }

    if (selectedIds.length > 0) {
      const result = normalizePostGeneratedImagesResult(response.data)

      if (!result) {
        return { success: false, error: 'Invalid response from server.' }
      }

      const variants = mapGeneratedImagesResponse(result)
      const selectedFromResponse = collectSelectedImageIdsFromResult(result)

      if (!selectedFromResponse.length) {
        return { success: false, error: 'No selected images returned from server.' }
      }

      const nextMedia = applyGeneratedVariantsToMedia(media, variants, selectedFromResponse).map(
        (file) => (file.generating ? { ...file, generating: false } : file),
      )

      dispatch({ type: actions.applyGeneratedVariants, payload: nextMedia })
      setSelectedSubGroupId(null)
    } else {
      dispatch({ type: actions.clearMediaGenerating })
    }

    setCoCreateVariant('open')
    await checkStatus()

    return { success: true }
  }

  const handleGenerate = async (params) => {
    setSelectedSubGroupId(null)
    const response = await generateImages(params)

    if (response.success && response.groupId) {
      dispatch({
        type: actions.setMediaGenerating,
        payload: { id: response.groupId, count: params.headlines?.length ?? 0 },
      })
    }

    return response
  }

  const handleRemoveOriginal = (item) => {
    removeFile(item.id)
  }

  const handleRemoveSubGroup = (subGroupId) => {
    removeMediaGroup(subGroupId)
  }

  const handleReplaceOriginal = (item) => {
    dispatch({ type: actions.startReplace, payload: item })
    open()
  }

  const handleDeviceUpload = () => {
    if (readonly || isCampaignRunning || reachedMaxLimit) {
      return
    }

    open()
  }

  const handleLibraryUpload = () => {
    if (readonly || isCampaignRunning || reachedMaxLimit) {
      return
    }

    setIsLibraryPickerOpen(true)
  }

  const handleLibraryApply = (selectedItems) => {
    addLibraryItems(selectedItems)
  }

  const handleAddToCoCreateSlot = async (file, groupId, requiredRatio, originalId = null) => {
    await handleAddFileToCoCreateGroup(file, groupId, requiredRatio, originalId)
  }

  const handleAddLibraryToCoCreateSlot = (item, groupId, requiredRatio, originalId = null) => {
    addLibraryItemToCoCreateGroup(item, groupId, requiredRatio, originalId)
  }

  const handleReplaceLibraryInCoCreateSlot = (item, fileToReplaceId) => {
    replaceLibraryItemInCoCreateGroup(item, fileToReplaceId)
  }

  const handleReplaceInCoCreateSlot = async (file, fileToReplaceId, requiredRatio) => {
    await handleReplaceFileInCoCreateGroup(file, fileToReplaceId, requiredRatio)
  }

  const handleRemoveFromCoCreateSlot = async (fileId) => {
    await handleRemoveFileFromCoCreateGroup(fileId)
  }

  return (
    <div>
      {!!state.error && <p className="warning-red mt-2 text-left">{state.error}</p>}

      <MediaListV2
        media={media}
        selectiveMode={coCreateVariant === 'open'}
        selectedSubGroupId={selectedSubGroupId}
        setSelectedSubGroupId={setSelectedSubGroupId}
        isCoCreateReady={coCreateVariant === 'ready'}
        readonly={readonly}
        onRemoveOriginal={handleRemoveOriginal}
        onRemoveSubGroup={handleRemoveSubGroup}
        onReplaceOriginal={handleReplaceOriginal}
        onAddToCoCreateSlot={handleAddToCoCreateSlot}
        onAddLibraryToCoCreateSlot={handleAddLibraryToCoCreateSlot}
        onReplaceLibraryToCoCreateSlot={handleReplaceLibraryInCoCreateSlot}
        onReplaceInCoCreateSlot={handleReplaceInCoCreateSlot}
        onRemoveFromCoCreateSlot={handleRemoveFromCoCreateSlot}
        isCampaignRunning={isCampaignRunning}
        onViewGeneratedVariants={() => setGeneratedImagesModalOpen(true)}
      />

      {media.length > 0 && !readonly && (
        fetchStatusFailed ? (
          <ErrorMessage danger={true} style={{ justifyContent: 'flex-start' }}>
            {t('Recommendations.campaign.setup.coCreatePanel.fetchStatusError')}
          </ErrorMessage>
        ) : (
          <CoCreateImagePanel
            variant={coCreateVariant}
            setVariant={setCoCreateVariant}
            selectedSubGroupId={selectedSubGroupId}
            setSelectedSubGroupId={setSelectedSubGroupId}
            media={media}
            headlines={headlines}
            maxItems={maxItems}
            generatedMediaCount={liveGeneratedMediaCount}
            showGenerationError={generationFailed}
            onGenerate={handleGenerate}
            isCampaignRunning={isCampaignRunning}
            onRequestHeadlines={onRequestHeadlines}
            onViewVariants={() => setGeneratedImagesModalOpen(true)}
          />
        )
      )}

      <PortalModal
        isOpen={generatedImagesModalOpen}
        onClickOutside={() => !isSubmittingSelections && setGeneratedImagesModalOpen(false)}
        onClose={() => !isSubmittingSelections && setGeneratedImagesModalOpen(false)}
        title={t('Recommendations.campaign.setup.coCreatePanel.generatedImagesModal.title')}
        className="generated-images-portal-modal"
      >
        <GeneratedImagesModal
          variants={generatedVariants}
          closeModal={() => setGeneratedImagesModalOpen(false)}
          onSubmitSelections={async (selectedIds) => {
            setIsSubmittingSelections(true)

            try {
              return await handleSubmitGeneratedSelections(selectedIds)
            } finally {
              setIsSubmittingSelections(false)
            }
          }}
        />
      </PortalModal>

      {!readonly && (
        <UploadButtons
          hasMedia={media.length > 0}
          getInputProps={getInputProps}
          onDeviceUpload={handleDeviceUpload}
          onLibraryUpload={handleLibraryUpload}
          disabled={isCampaignRunning || reachedMaxLimit}
          isCampaignRunning={isCampaignRunning}
        />
      )}

      <LibraryPickerModal
        isOpen={isLibraryPickerOpen}
        onClose={() => setIsLibraryPickerOpen(false)}
        onApply={handleLibraryApply}
        selectionMode="multiple"
      />

      {expandedImage && (
        <PortalModal
          isOpen={!!expandedImage}
          className="full-height"
          onClickOutside={() => setExpandedImage(null)}
          title="Media Preview"
        >
          <MediaExpandedModal
            expandedImage={expandedImage}
            setExpandedImage={setExpandedImage}
            onBack={onBack}
            onNext={onNext}
            isFirstItem={isFirstItem}
            isLastItem={isLastItem}
          />
        </PortalModal>
      )}
    </div>
  )
}

MediaUpload.propTypes = {
  initialMedia: PropTypes.arrayOf(object),
  onChange: PropTypes.func.isRequired,
  onClearValidationErrors: PropTypes.func,
  maxItems: PropTypes.number,
  generatedMediaCount: PropTypes.number,
  readonly: PropTypes.bool,
  validationError: PropTypes.bool,
  isCampaignRunning: PropTypes.bool,
  eventId: PropTypes.string,
  tc: PropTypes.string,
  tcRunId: PropTypes.number,
  headlines: PropTypes.arrayOf(PropTypes.string),
  onRequestHeadlines: PropTypes.func,
}

export default MediaUpload
